import * as React from 'react';
import {
  Box,
  Pagination,
  Tooltip,
} from '@mui/material';
import { connect } from "react-redux";
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import AddIcon from '@mui/icons-material/Add';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { searchEvents } from '../../app/store/actions/eventActions';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformEventCard,
  PlatformStatsGrid,
  PlatformStatCard,
  PlatformButton,
  PlatformSearchBar,
  PlatformSearchRow,
  PlatformSectionTitle,
  PlatformEmptyState,
  PlatformLoadingBlock,
} from '../../app/components/platform';
import {
  platformColors,
  platformFonts,
} from '../../app/components/tickitPlatformTheme';

export const PartnerEvents = ({ events, searchEvents, user }) => {
  const navigate = useNavigate();
  const initialState = {
    validEvents: user?.events,
    search: "",
    page: 1,
    limit: 10,
  };

  React.useEffect(() => {
    searchEvents(initialState);
  }, []);

  const [formData, setFormData] = React.useState(initialState);
  const { search, page } = formData;
  const role = "Partner";
  const docs = events?.results?.docs || [];
  const eventCount = docs.length;
  const upcomingCount = docs.filter((e) => e?.date && moment(e.date).isAfter(moment())).length;
  const corporateCount = docs.filter((e) => e?.corporate).length;
  const partnerTitle = user?.companyName || user?.name || user?.username || "Partner";

  const handlePageChange = (_event, value) => {
    const next = { ...formData, page: value };
    setFormData(next);
    searchEvents(next);
  };

  const handleClearFilter = () => {
    setFormData(initialState);
    searchEvents(initialState);
  };

  const handleSearchFilter = () => {
    searchEvents({ ...formData, page: 1 });
    setFormData({ ...formData, page: 1 });
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') handleSearchFilter();
  };

  const handleSearchChange = (e) => {
    setFormData({ ...formData, search: e.target.value });
  };

  const searchRow = (
    <>
      <PlatformSearchBar
        value={search}
        onChange={handleSearchChange}
        onKeyPress={handleSearchKeyPress}
        placeholder="Search events by name or venue..."
      />
      <PlatformButton variant="ghost" onClick={handleClearFilter}>
        Clear
      </PlatformButton>
      <PlatformButton variant="primary" onClick={handleSearchFilter}>
        Search
      </PlatformButton>
    </>
  );

  return (
    <PlatformPage>
      <PlatformPageHeader
        title={partnerTitle}
        subtitle="Module 1 — RSVP & QR Invitation"
        actions={
          <>
            <Tooltip title="Bulk guest import — coming soon">
              <span>
                <PlatformButton variant="ghost" size="small" startIcon={<UploadOutlinedIcon />} disabled>
                  Import guests
                </PlatformButton>
              </span>
            </Tooltip>
            <PlatformButton
              variant="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/events/create')}
            >
              New event
            </PlatformButton>
          </>
        }
      />

      <PlatformStatsGrid columns={3}>
        <PlatformStatCard
          label="My events"
          value={events?.results?.totalDocs ?? eventCount}
          delta={`${upcomingCount} upcoming`}
          deltaTone="up"
          icon={<EventOutlinedIcon />}
          iconBg={platformColors.brandPale}
          iconColor={platformColors.brand}
        />
        <PlatformStatCard
          label="Corporate / RSVP"
          value={corporateCount}
          delta={eventCount ? `${Math.round((corporateCount / eventCount) * 100)}% of page` : "Invitation events"}
          deltaTone="up"
          icon={<CheckCircleOutlineIcon />}
          iconBg="#DBEAFE"
          iconColor="#1E40AF"
        />
        <PlatformStatCard
          label="QR check-in"
          value="Live"
          delta="Open scanner from event dashboard"
          icon={<QrCodeScannerIcon />}
          iconBg="#FEF3C7"
          iconColor="#92400E"
        />
      </PlatformStatsGrid>

      <PlatformSearchRow sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
        {searchRow}
      </PlatformSearchRow>

      <PlatformSectionTitle>My Events</PlatformSectionTitle>

      {events?.isLoading ? (
        <PlatformLoadingBlock label="Loading events…" />
      ) : docs.length > 0 ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 2,
              mb: 3,
            }}
          >
            {docs.map((event) => (
              <PlatformEventCard key={event._id} event={event} role={role} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={events?.results?.pages || 1}
              page={page}
              onChange={handlePageChange}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontFamily: platformFonts.body,
                  fontSize: '13.5px',
                  color: 'var(--platform-text-2)',
                },
                '& .Mui-selected': {
                  backgroundColor: `${platformColors.brandPale} !important`,
                  color: `${platformColors.brand} !important`,
                  fontWeight: 700,
                },
              }}
            />
          </Box>
        </>
      ) : (
        <PlatformEmptyState
          title="No events found"
          description="Try adjusting your search or create a new event."
          actionLabel="New event"
          onAction={() => navigate('/events/create')}
        />
      )}
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  events: state?.events,
});

const mapDispatchToProps = { searchEvents };

export default connect(mapStateToProps, mapDispatchToProps)(PartnerEvents);
